/**
 * GLSL ES 3.00 sources. One full-screen triangle; every pixel is mapped
 *   screen → model plane → Poincaré disk → (inverse view) world → folded into the domain,
 * then coloured from the paint layer. The fold is the same loop as `fold()` in
 * src/geometry/triangle-group.ts.
 */

export const VERTEX_SHADER = /* glsl */ `#version 300 es
in vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

export const MAX_FOLD_ITERATIONS = 160;

export const FRAGMENT_SHADER = /* glsl */ `#version 300 es
precision highp float;
precision highp int;

out vec4 outColor;

uniform vec2 u_full;        // full image size (device px)
uniform vec2 u_offset;      // offset of this tile inside the full image (bottom-left origin)
uniform vec2 u_camCenter;   // model-plane point at the image centre
uniform float u_camScale;   // model units per device px
uniform int u_model;        // 0 disk · 1 Klein · 2 half-plane · 3 band
uniform float u_morph;      // 0 = disk picture … 1 = the model
uniform vec4 u_view;        // inverse view isometry (a.xy, b.xy): w = (a z + b)/(b̄ z + ā)

uniform vec2 u_n1;          // outward normal of mirror m1
uniform vec3 u_circle;      // mirror m2: centre.xy, radius
uniform int u_rotation;     // 1 = rotation group (fold into W = F ∪ m0 F)
uniform int u_scheme;       // 0 single · 1 reflection parity · 2 rotation classes
uniform int u_k;            // number of colour classes
uniform uint u_pair[9];     // ρ(m_a m_b) packed, index 3a + b
uniform uint u_baseInv;     // ρ(anchor)⁻¹ packed

uniform sampler2D u_motif;
uniform vec2 u_texOrigin;
uniform vec2 u_texScale;    // 1 / box size
uniform vec2 u_texSize;     // texels

uniform vec3 u_ground;
uniform vec3 u_pal[4];      // ink, vermilion, gold, ivory
uniform vec3 u_classGround[8];
uniform vec3 u_classInk[8];
uniform vec3 u_avg;         // average colour of the pattern (for sub-pixel tiles)
uniform float u_triSize;    // hyperbolic size of the triangle

uniform int u_grid;         // 0 off · 1 tile edges · 2 all mirrors
uniform vec3 u_gold;
uniform vec3 u_line;        // dark line colour
uniform float u_tileOutline;
uniform float u_domainOutline;
uniform float u_focus;      // editor: dim every copy except the base domain
uniform float u_dpr;
uniform vec4 u_bg;          // colour outside the plane (straight alpha)

vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
vec2 cdiv(vec2 a, vec2 b) { return vec2(a.x * b.x + a.y * b.y, a.y * b.x - a.x * b.y) / dot(b, b); }
vec2 cconj(vec2 a) { return vec2(a.x, -a.y); }

vec2 ctanh(vec2 a) {
  float x2 = clamp(2.0 * a.x, -30.0, 30.0);
  float y2 = 2.0 * a.y;
  return vec2(sinh(x2), sin(y2)) / (cosh(x2) + cos(y2));
}

// Model plane → disk. 'valid' is false when w is outside the model's picture of the plane.
vec2 toDisk(vec2 w, out bool valid) {
  valid = true;
  if (u_model == 1) {
    float s = 1.0 - dot(w, w);
    if (s <= 0.0) { valid = false; return w; }
    return w / (1.0 + u_morph * sqrt(s));
  }
  if (u_model == 2) {
    return cdiv(w - vec2(0.0, 1.0), vec2(0.0, 1.0) + u_morph * w);
  }
  if (u_model == 3) {
    if (u_morph < 1e-5) return w;
    float s = 1.0 + u_morph * (1.2732395447 - 1.0);
    vec2 a = w * (u_morph / s);
    if (abs(a.y) >= 1.5703) { valid = false; return w * 4.0; }
    return ctanh(a) / u_morph;
  }
  return w;
}

uint permCompose(uint S, uint T) {
  uint r = 0u;
  for (int j = 0; j < 8; j++) {
    uint t = (T >> uint(4 * j)) & 15u;
    r |= ((S >> (t * 4u)) & 15u) << uint(4 * j);
  }
  return r;
}

float lineAlpha(float distPx, float halfWidthPx) {
  return clamp(halfWidthPx + 0.5 - distPx, 0.0, 1.0);
}

void main() {
  vec2 px = gl_FragCoord.xy + u_offset;
  vec2 m = u_camCenter + (px - 0.5 * u_full) * u_camScale;
  bool valid;
  vec2 z = toDisk(m, valid);
  float r2 = dot(z, z);
  // Euclidean size of a pixel in disk units (derivatives taken in uniform control flow)
  float pixDisk = max(max(length(dFdx(z)), length(dFdy(z))), 1e-9);
  float rz = sqrt(r2);
  // Signed distance (device px, > 0 inside) to the boundary at infinity. Exact in model
  // coordinates once a model is fully shown; estimated from the disk mapping during a morph.
  float edgePx;
  bool inside;
  if (u_model == 0 || u_morph >= 0.999) {
    float d = u_model == 2 ? m.y : (u_model == 3 ? 1.0 - abs(m.y) : 1.0 - length(m));
    edgePx = d / u_camScale;
    inside = edgePx > 0.0 && valid && r2 < 1.0;
  } else {
    edgePx = (1.0 - rz) / pixDisk;
    inside = valid && r2 < 1.0 && edgePx > 0.0;
  }

  if (!inside) {
    float outside = edgePx < 0.0 ? -edgePx : (valid ? 0.0 : 1e4);
    float ring = lineAlpha(outside, 0.6 * u_dpr);
    float halo = exp(-outside / (7.0 * u_dpr)) * 0.22;
    vec3 col = mix(u_bg.rgb, u_gold, clamp(ring + halo, 0.0, 1.0));
    float a = max(u_bg.a, clamp(ring + halo, 0.0, 1.0));
    outColor = vec4(col * a, a);
    return;
  }

  // Hyperbolic size of this pixel.
  float footH = pixDisk * 2.0 / (1.0 - r2);
  float lw = u_dpr; // one CSS pixel in device pixels

  // World point: w = V⁻¹(z)
  vec2 va = u_view.xy;
  vec2 vb = u_view.zw;
  vec2 w = cdiv(cmul(va, z) + vb, cmul(cconj(vb), z) + cconj(va));

  vec3 col;
  float tileA = 0.0, gridA = 0.0, domA = 0.0;
  float fade = smoothstep(0.55, 1.9, footH / u_triSize);

  if (fade >= 1.0) {
    col = u_avg;
  } else {
    int count = 0;
    int m2c = 0;
    int first = -1;
    int pending = -1;
    float edgeH = 0.0; // distance to the edge crossed by the first reflection in m2
    uint P = 0x76543210u;
    bool track = (u_scheme == 2);
    float rr = u_circle.z * u_circle.z;
    for (int i = 0; i < ${MAX_FOLD_ITERATIONS}; i++) {
      int mir = -1;
      if (w.y < 0.0) {
        w.y = -w.y;
        mir = 0;
      } else {
        float d = dot(w, u_n1);
        if (d > 0.0) {
          w -= 2.0 * d * u_n1;
          mir = 1;
        } else {
          vec2 dc = w - u_circle.xy;
          float q = dot(dc, dc);
          if (q < rr) {
            if (m2c == 0) edgeH = asinh((rr - q) / (u_circle.z * (1.0 - dot(w, w))));
            w = u_circle.xy + dc * (rr / q);
            mir = 2;
            m2c++;
          }
        }
      }
      if (mir < 0) break;
      if (count == 0) first = mir;
      count++;
      if (track) {
        if (pending < 0) {
          pending = mir;
        } else {
          P = permCompose(u_pair[3 * mir + pending], P);
          pending = -1;
        }
      }
    }
    int parity = count & 1;
    vec2 wf = w; // folded into the triangle F
    if (parity == 1) {
      if (track && pending >= 0) P = permCompose(u_pair[pending], P);
      if (u_rotation == 1) w.y = -w.y;
    }
    int cls = 0;
    if (track) {
      for (int j = 0; j < 8; j++) {
        if (j < u_k && ((P >> uint(4 * j)) & 15u) == 0u) cls = j;
      }
      cls = int((u_baseInv >> uint(4 * cls)) & 15u);
    }

    // Paint layer, with the mip level chosen from the pixel's hyperbolic footprint.
    vec2 uv = (w - u_texOrigin) * u_texScale;
    float footDisk = footH * (1.0 - dot(w, w)) * 0.5;
    float texels = footDisk * max(u_texScale.x * u_texSize.x, u_texScale.y * u_texSize.y);
    vec4 t = textureLod(u_motif, uv, max(log2(max(texels, 1e-6)), 0.0));

    vec3 ground = u_ground;
    vec3 ink = u_pal[0];
    if (u_scheme == 1 && u_rotation == 0 && parity == 1) {
      ground = u_pal[0];
      ink = u_ground;
    } else if (track) {
      ground = u_classGround[cls];
      ink = u_classInk[cls];
    }
    float ivory = max(t.a - t.r - t.g - t.b, 0.0);
    col = ground * (1.0 - t.a) + ink * t.r + u_pal[1] * t.g + u_pal[2] * t.b + u_pal[3] * ivory;

    bool base = (count == 0) || (u_rotation == 1 && count == 1 && first == 0);
    if (u_focus > 0.0 && !base) col = mix(col, u_ground, 0.7 * u_focus);

    // Distances (hyperbolic → px) from the folded point to the three mirrors.
    float om = 1.0 - dot(wf, wf);
    float d0 = asinh(2.0 * abs(wf.y) / om) / footH;
    float d1 = asinh(2.0 * abs(dot(wf, u_n1)) / om) / footH;
    vec2 dc = wf - u_circle.xy;
    float d2 = asinh(abs(dot(dc, dc) - rr) / (u_circle.z * om)) / footH;

    if (u_grid == 1) gridA = lineAlpha(d2, 0.5 * lw);
    if (u_grid == 2) gridA = max(lineAlpha(d2, 0.55 * lw), 0.75 * lineAlpha(min(d0, d1), 0.4 * lw));
    if (m2c == 0) tileA = lineAlpha(d2, 1.1 * lw) * u_tileOutline;
    else if (m2c == 1) tileA = lineAlpha(edgeH / footH, 1.1 * lw) * u_tileOutline;
    if (base) {
      float dd = u_rotation == 1 ? min(d1, d2) : min(min(d0, d1), d2);
      domA = lineAlpha(dd, 0.6 * lw * max(1.0, u_domainOutline)) * min(u_domainOutline, 1.0);
    }
    gridA *= 1.0 - fade;
    tileA *= 1.0 - smoothstep(0.6, 1.4, footH / u_triSize);
    col = mix(col, u_avg, fade);
  }

  // a faint darkening toward the circle at infinity gives the picture depth
  col *= 1.0 - 0.1 * smoothstep(0.82, 1.0, rz);
  col = mix(col, u_gold * 0.92, gridA * 0.85);
  col = mix(col, u_line, domA * 0.8);
  col = mix(col, u_line, tileA * 0.9);
  // inner gold rim at the boundary circle
  col = mix(col, u_gold, lineAlpha(edgePx, 0.6 * lw));
  outColor = vec4(col, 1.0);
}
`;
